import type { CourseSession, CourseSessionDateBlock } from '../types/crm';
import { buildSessionReferenceIndex, sessionReferenceCode } from '../components/dashboard/trainingCalendarUtils';
import { formatDateUk } from './paymentFormUtils';

export function sessionFirstDateIso(session: Pick<CourseSession, 'dateBlocks'>): string | null {
  const blocks = Array.isArray(session.dateBlocks) ? session.dateBlocks : [];
  const sorted = [...blocks].sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  return sorted[0]?.date || null;
}

/** Normalized schedule signature (all dates + start/end times). */
export function sessionDateBlocksSignature(session: Pick<CourseSession, 'dateBlocks'>): string {
  const blocks = Array.isArray(session.dateBlocks) ? session.dateBlocks : [];
  const sorted = [...blocks].sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  return JSON.stringify(
    sorted.map((b) => ({
      date: b.date || '',
      startTime: b.startTime ?? null,
      endTime: b.endTime ?? null,
    }))
  );
}

/** Grouping key for likely duplicate/orphan session records. */
export function duplicateSessionGroupKey(
  session: Pick<CourseSession, 'courseName' | 'language' | 'dateBlocks' | 'locationId'>
): string {
  const date = sessionFirstDateIso(session);
  return [
    String(session.courseName || '').trim().toLowerCase(),
    String(session.language || '').trim().toLowerCase(),
    date || '',
    String(session.locationId || '').trim(),
    sessionDateBlocksSignature(session),
  ].join('|');
}

export function sessionsAreLikelyDuplicates(
  a: Pick<CourseSession, 'id' | 'courseName' | 'language' | 'dateBlocks' | 'locationId'>,
  b: Pick<CourseSession, 'id' | 'courseName' | 'language' | 'dateBlocks' | 'locationId'>
): boolean {
  if (a.id === b.id) return false;
  return duplicateSessionGroupKey(a) === duplicateSessionGroupKey(b);
}

export type FindDuplicateSessionGroupsOptions = {
  /** Active roster seat count per session — used to surface empty ghost records only. */
  rosterCountBySessionId?: Map<string, number>;
};

/**
 * Scheduled sessions that share course, language, date, location, and full schedule.
 * Only returns groups where at least one record has no roster and another has bookings
 * (empty ghost alongside a live session).
 */
export function findDuplicateSessionGroups(
  sessions: CourseSession[],
  options?: FindDuplicateSessionGroupsOptions
): CourseSession[][] {
  const scheduled = sessions.filter((s) => (s.status || 'scheduled') !== 'cancelled');
  const groups = new Map<string, CourseSession[]>();
  for (const session of scheduled) {
    const key = duplicateSessionGroupKey(session);
    const list = groups.get(key) || [];
    list.push(session);
    groups.set(key, list);
  }

  const rosterCounts = options?.rosterCountBySessionId;
  return [...groups.values()].filter((group) => {
    if (group.length <= 1) return false;
    if (!rosterCounts) return false;
    const counts = group.map((s) => rosterCounts.get(s.id) ?? 0);
    const hasEmpty = counts.some((c) => c === 0);
    const hasPopulated = counts.some((c) => c > 0);
    return hasEmpty && hasPopulated;
  });
}

function formatDateBlocksUk(blocks: CourseSessionDateBlock[] | undefined): string {
  if (!blocks?.length) return 'No dates set';
  const sorted = [...blocks].sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  return sorted.map((b) => formatDateUk(b.date)).join(' · ');
}

/** Distinct label for copy/reschedule session pickers (ref + last-edited date). */
export function formatSessionTargetLabel(session: CourseSession, refIndex?: number): string {
  const lang = String(session.language || '').trim();
  const courseLabel = lang ? `${session.courseName} ${lang}` : session.courseName;
  const ref = sessionReferenceCode(session.id, refIndex);
  const updated = session.updatedAt || session.createdAt;
  const updatedLabel = updated ? formatDateUk(updated.slice(0, 10)) : '';
  const dates = formatDateBlocksUk(session.dateBlocks);
  return `${courseLabel} — ${dates} · ${ref}${updatedLabel ? ` · edited ${updatedLabel}` : ''}`;
}

export function sessionReferenceCodesForGroup(
  sessions: CourseSession[],
  allSessionsForIndex: CourseSession[]
): string {
  const refIndex = buildSessionReferenceIndex(allSessionsForIndex);
  return sessions.map((s) => sessionReferenceCode(s.id, refIndex.get(s.id))).join(', ');
}
